import {
  DocumentDuplicateIcon,
  Cog6ToothIcon,
  PencilSquareIcon,
  PlusIcon,
  StarIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import {
  StarIcon as StarIconSolid,
} from "@heroicons/react/24/solid";
import axios from "axios";
import { CheckIcon } from "@heroicons/react/24/outline";
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import toast from "react-hot-toast";
import { ReportTemplate, ReportTemplateContent } from "../../../db/schema";
import Button from "../../common/Button";
import Tooltip from "../../common/Tooltip";
import ReportTemplateEditor from "./ReportTemplateEditor";

export interface ReportTemplateManagerHandle {
  save: () => Promise<void>;
}

interface ReportTemplateManagerProps {
  onBack?: () => void;
  /** Called when content changes, so parent can track dirty state */
  onContentChange?: (content: ReportTemplateContent) => void;
  /** Base path for API calls. Default: "/api/report-templates" */
  apiBasePath?: string;
}

function TemplateDetailsEditor({
  name: initialName,
  description: initialDescription,
  onSave,
  onCancel,
}: {
  name: string;
  description: string;
  onSave: (name: string, description: string) => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState(initialName);
  const [description, setDescription] = useState(initialDescription);

  return (
    <div
      className="rounded-xl border border-blue-200 dark:border-blue-800 bg-blue-50/50 dark:bg-blue-900/40 p-4 mb-4 space-y-3"
      data-tour="template-details"
    >
      <div>
        <label className="block text-xs font-medium text-slate-600 dark:text-base-content/70 mb-1">
          Template Name
        </label>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g., Domestic Violence Report, Traffic Stop..."
          className="w-full px-3 py-1.5 text-sm border border-base-300 rounded-lg bg-base-100 focus:outline-none focus:border-blue-400 text-slate-700 dark:text-base-content/80 placeholder:text-slate-300 dark:placeholder:text-base-content/30"
          autoFocus
          onKeyDown={(e) => {
            if (e.key === "Escape") onCancel();
          }}
        />
      </div>
      <div>
        <label className="block text-xs font-medium text-slate-600 dark:text-base-content/70 mb-1">
          Description <span className="text-slate-400 dark:text-base-content/40 font-normal">(optional)</span>
        </label>
        <input
          type="text"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Brief description of when to use this template..."
          className="w-full px-3 py-1.5 text-sm border border-base-300 rounded-lg bg-base-100 focus:outline-none focus:border-blue-400 text-slate-700 dark:text-base-content/80 placeholder:text-slate-300 dark:placeholder:text-base-content/30"
          onKeyDown={(e) => {
            if (e.key === "Enter" && name.trim()) onSave(name, description);
            if (e.key === "Escape") onCancel();
          }}
        />
      </div>
      <div className="flex items-center gap-2">
        <Button
          variant="primary"
          size="xs"
          onClick={() => onSave(name, description)}
          disabled={!name.trim()}
        >
          <CheckIcon className="w-3.5 h-3.5" />
          Save
        </Button>
        <Button variant="outline" size="xs" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

const ReportTemplateManager = forwardRef<ReportTemplateManagerHandle, ReportTemplateManagerProps>(
  ({ onBack, onContentChange: onContentChangeProp, apiBasePath = "/api/report-templates" }, ref) => {
  const [templates, setTemplates] = useState<ReportTemplate[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [defaultTemplateId, setDefaultTemplateId] = useState<string | null>(null);
  const [renamingTemplateId, setRenamingTemplateId] = useState<string | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const newlyCreatedId = useRef<string | null>(null);
  const actionsDropdownRef = useRef<HTMLDivElement>(null);

  // Track content via ref (avoids re-renders)
  const compactContentRef = useRef<ReportTemplateContent>({ header: "", footer: "", body: "" });

  const fetchTemplates = useCallback(async () => {
    try {
      const { data } = await axios.get(`${apiBasePath}/templates`);
      setTemplates(data.templates);
      if (data.defaultTemplateId !== undefined) {
        setDefaultTemplateId(data.defaultTemplateId);
      }
    } catch (error) {
      console.error("Error fetching templates:", error);
      toast.error("Failed to load templates");
    } finally {
      setIsLoading(false);
    }
  }, [apiBasePath]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  // Sync contentRef when selected template changes
  const initialContentReported = useRef(false);
  useEffect(() => {
    const selected = templates.find(t => t.id === selectedTemplateId);
    if (selected) {
      compactContentRef.current = selected.content;
    } else if (templates.length === 0) {
      compactContentRef.current = { header: "", footer: "", body: "" };
    }
    // Report initial content so parent can set its baseline
    if (!initialContentReported.current && templates.length <= 1) {
      const content = templates.length === 1
        ? templates[0].content
        : { header: "", footer: "", body: "" };
      onContentChangeProp?.(content);
      initialContentReported.current = true;
    }
  }, [templates, selectedTemplateId, onContentChangeProp]);

  // Auto-select default template when templates load
  useEffect(() => {
    if (templates.length > 0 && !selectedTemplateId) {
      const defaultMatch = defaultTemplateId ? templates.find(t => t.id === defaultTemplateId) : null;
      setSelectedTemplateId(defaultMatch?.id ?? templates[0].id);
    }
    // If selected template was deleted, select the first one
    if (selectedTemplateId && !templates.find((t) => t.id === selectedTemplateId)) {
      setSelectedTemplateId(templates.length > 0 ? templates[0].id : null);
    }
  }, [templates, selectedTemplateId, defaultTemplateId]);

  // Expose save() for external callers (e.g. PreferencesForm "Save Preferences")
  useImperativeHandle(ref, () => ({
    save: async () => {
      const content = compactContentRef.current;

      if (selectedTemplateId) {
        const template = templates.find(t => t.id === selectedTemplateId);
        // Skip saving agency templates from the officer view
        if (template && !isAgencyTemplate(template)) {
          await axios.patch(
            `${apiBasePath}/templates/${template.id}`,
            {
              name: template.name,
              description: template.description || "",
              content,
              is_default: template.is_default,
            }
          );
        }
      } else if (templates.length === 0) {
        await axios.post(`${apiBasePath}/templates`, {
          name: "Default Template",
          description: "",
          content,
          is_default: true,
        });
      }
      await fetchTemplates();
    },
  }), [templates, selectedTemplateId, fetchTemplates, apiBasePath]);

  const handleDelete = async (id: string) => {
    try {
      const wasDefault = defaultTemplateId === id;
      await axios.delete(`${apiBasePath}/templates/${id}`);
      toast.success("Template deleted");
      await fetchTemplates();
      setDeleteConfirmId(null);
      // If we deleted the default, assign default to the first remaining template
      if (wasDefault) {
        const remaining = templates.filter(t => t.id !== id);
        if (remaining.length > 0) {
          await axios.patch(`${apiBasePath}/templates/${remaining[0].id}/default`);
          await fetchTemplates();
        }
      }
    } catch (error) {
      console.error("Error deleting template:", error);
      toast.error("Failed to delete template");
    }
  };

  const handleToggleDefault = async (id: string) => {
    try {
      const { data } = await axios.patch(`${apiBasePath}/templates/${id}/default`);
      await fetchTemplates();
      if (isAgencyScope) {
        toast.success("Template set as default for all officers");
      } else {
        toast.success(data.defaultTemplateId ? "Default template updated" : "Default template removed");
      }
    } catch (error) {
      console.error("Error toggling default:", error);
      toast.error("Failed to update default template");
    }
  };

  const handleDuplicate = async (template: ReportTemplate) => {
    try {
      await axios.post(`${apiBasePath}/templates`, {
        name: `${template.name} (Copy)`,
        description: template.description || "",
        content: template.content,
        is_default: false,
      });
      toast.success("Template duplicated");
      await fetchTemplates();
    } catch (error) {
      console.error("Error duplicating template:", error);
      toast.error("Failed to duplicate template");
    }
  };

  const handleEditDetails = async (id: string, newName: string, newDescription: string) => {
    const trimmed = newName.trim();
    setRenamingTemplateId(null);
    newlyCreatedId.current = null;
    if (!trimmed) return;
    const template = templates.find(t => t.id === id);
    if (!template) return;
    if (template.name === trimmed && (template.description || "") === newDescription) return;
    try {
      await axios.patch(`${apiBasePath}/templates/${id}`, {
        name: trimmed,
        description: newDescription,
        content: template.content,
        is_default: template.is_default,
      });
      await fetchTemplates();
    } catch (error) {
      console.error("Error updating template:", error);
      toast.error("Failed to update template");
    }
  };

  const handleCreateTemplate = async () => {
    try {
      const oldIds = new Set(templates.map(t => t.id));
      await axios.post(`${apiBasePath}/templates`, {
        name: "Untitled Template",
        description: "",
        content: { header: "", footer: "", body: "" },
        is_default: false,
      });
      const { data } = await axios.get(`${apiBasePath}/templates`);
      setTemplates(data.templates);
      const newTemplate = data.templates.find((t: ReportTemplate) => !oldIds.has(t.id));
      if (newTemplate) {
        newlyCreatedId.current = newTemplate.id;
        setSelectedTemplateId(newTemplate.id);
        setRenamingTemplateId(newTemplate.id);
      }
    } catch (error) {
      console.error("Error creating template:", error);
      toast.error("Failed to create template");
    }
  };

  const handleContentChange = (content: ReportTemplateContent) => {
    compactContentRef.current = content;
    onContentChangeProp?.(content);
  };

  const isContentDirty = useCallback(() => {
    if (!selectedTemplateId) return false;
    const template = templates.find(t => t.id === selectedTemplateId);
    if (!template) return false;
    return JSON.stringify(compactContentRef.current) !== JSON.stringify(template.content);
  }, [selectedTemplateId, templates]);

  const handleSelectTemplate = useCallback((id: string) => {
    if (id === selectedTemplateId) return;
    if (isContentDirty()) {
      const leave = window.confirm("You have unsaved changes. Are you sure you want to switch templates?");
      if (!leave) return;
    }
    setSelectedTemplateId(id);
  }, [selectedTemplateId, isContentDirty]);

  const selectedTemplate = templates.find((t) => t.id === selectedTemplateId);
  const renamingTemplate = renamingTemplateId ? templates.find((t) => t.id === renamingTemplateId) : null;
  // Agency templates (user_id is null) are read-only in the officer view (default apiBasePath)
  const isAgencyTemplate = (t: ReportTemplate) => t.user_id === null && apiBasePath === "/api/report-templates";
  const selectedIsReadOnly = selectedTemplate ? isAgencyTemplate(selectedTemplate) : false;
  const showAsDefault = (t: ReportTemplate) => t.id === defaultTemplateId;
  // Agency-scoped paths (whether the dept-admin view or the super-admin
  // agency-management view) treat the default as an imperative push to every
  // officer rather than a personal pick.
  const isAgencyScope =
    apiBasePath === "/api/agency/report-templates" ||
    /\/agency-management\/agencies\/[^/]+\/report-templates$/.test(apiBasePath);

  return (
    <div data-tour="template-manager">
      {/* Template tabs */}
      {!isLoading && templates.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5 mb-4">
          {[...templates].sort((a, b) => (showAsDefault(b) ? 1 : 0) - (showAsDefault(a) ? 1 : 0)).map((template) => (
            <Tooltip
              key={template.id}
              overlay={template.description || template.name}
              placement="top"
            >
              <Button
                variant={selectedTemplateId === template.id ? "primary" : "secondary"}
                data-template-name={template.name}
                data-template-default={showAsDefault(template) ? "true" : undefined}
                size="xs"
                onClick={() => handleSelectTemplate(template.id)}
              >
                {showAsDefault(template) && (
                  <StarIconSolid className="w-3 h-3 text-amber-400" />
                )}
                {isAgencyTemplate(template) && (
                  <span className="text-[10px] font-semibold bg-blue-100 dark:bg-blue-900/60 text-blue-600 dark:text-blue-300 px-1 py-0.5 rounded">Dept</span>
                )}
                <span>{template.name}</span>
              </Button>
            </Tooltip>
          ))}
          <Button
            variant="outline"
            size="xs"
            onClick={handleCreateTemplate}
            data-tour="template-new"
          >
            <PlusIcon className="w-3.5 h-3.5" />
            New Template
          </Button>

          {/* Actions dropdown for selected template */}
          {selectedTemplate && (
            <div
              className="dropdown dropdown-end ml-auto"
              ref={actionsDropdownRef}
              data-tour="template-actions"
            >
              <Button
                variant="clean"
                size="xs"
                tabIndex={0}
                onClick={() => {
                  setDeleteConfirmId(null);
                  const content = actionsDropdownRef.current?.querySelector(
                    ".dropdown-content",
                  ) as HTMLElement;
                  if (content?.matches(":focus-within")) {
                    content.blur();
                  }
                }}
              >
                <Cog6ToothIcon className="w-5 h-5" />
              </Button>
              <ul
                tabIndex={0}
                className="dropdown-content menu bg-base-100 rounded-lg z-[1] w-48 p-2 shadow mt-1"
              >
                {!selectedIsReadOnly && (
                  <li className="m-0 p-0">
                    <p
                      onClick={() => {
                        setRenamingTemplateId(selectedTemplate.id);
                        // Close dropdown by blurring all focusable elements inside it
                        actionsDropdownRef.current?.querySelectorAll("[tabindex]").forEach(el => {
                          (el as HTMLElement).blur();
                        });
                        (document.activeElement as HTMLElement)?.blur();
                      }}
                      className="hover:bg-gray-100 dark:hover:bg-base-200 m-0 font-medium flex items-center gap-2"
                    >
                      <PencilSquareIcon className="w-4 h-4" />
                      Rename
                    </p>
                  </li>
                )}
                <li className="m-0 p-0">
                  <p
                    onClick={() => handleDuplicate(selectedTemplate)}
                    className="hover:bg-gray-100 dark:hover:bg-base-200 m-0 font-medium flex items-center gap-2"
                  >
                    <DocumentDuplicateIcon className="w-4 h-4" />
                    Duplicate
                  </p>
                </li>
                {(isAgencyScope || !showAsDefault(selectedTemplate)) && (
                  <li className="m-0 p-0">
                    <p
                      data-template-action="default"
                      onClick={() => handleToggleDefault(selectedTemplate.id)}
                      className="hover:bg-gray-100 dark:hover:bg-base-200 m-0 font-medium flex items-center gap-2"
                    >
                      <StarIcon className="w-4 h-4" />
                      {isAgencyScope ? "Default for all" : "Set as default"}
                    </p>
                  </li>
                )}
                {!selectedIsReadOnly && (
                  <li className="m-0 p-0">
                    <p
                      onClick={() => {
                        if (deleteConfirmId === selectedTemplate.id) {
                          handleDelete(selectedTemplate.id);
                        } else {
                          setDeleteConfirmId(selectedTemplate.id);
                        }
                      }}
                      className="hover:bg-gray-100 dark:hover:bg-base-200 m-0 font-medium flex items-center gap-2 text-red-500"
                    >
                      <TrashIcon className="w-4 h-4" />
                      {deleteConfirmId === selectedTemplate.id
                        ? "Confirm delete"
                        : "Delete"}
                    </p>
                  </li>
                )}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Edit name/description card */}
      {renamingTemplate && (
        <TemplateDetailsEditor
          key={`rename-${renamingTemplateId}`}
          name={renamingTemplate.name}
          description={renamingTemplate.description || ""}
          onSave={(name, description) => handleEditDetails(renamingTemplate.id, name, description)}
          onCancel={() => {
            const idToDelete = newlyCreatedId.current;
            setRenamingTemplateId(null);
            newlyCreatedId.current = null;
            if (idToDelete) {
              handleDelete(idToDelete);
            }
          }}
        />
      )}

      {/* Selected template editor */}
      {isLoading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className="h-20 bg-slate-100 dark:bg-base-200 rounded-xl animate-pulse"
            />
          ))}
        </div>
      ) : selectedTemplate ? (
        <ReportTemplateEditor
          key={selectedTemplate.id}
          template={{
            id: selectedTemplate.id,
            name: selectedTemplate.name,
            description: selectedTemplate.description,
            content: selectedTemplate.content,
            is_default: selectedTemplate.is_default ?? false,
          }}
          onContentChange={handleContentChange}
          readOnly={selectedIsReadOnly}
        />
      ) : (
        <ReportTemplateEditor
          onContentChange={handleContentChange}
        />
      )}
    </div>
  );
});

export default ReportTemplateManager;
